import React, { useEffect, useState } from 'react';
import { Checkbox, DefaultButton, Dropdown, IDropdownOption, TextField, mergeStyleSets } from '@fluentui/react';
import DatePickerComponent from '../date-picker/date-picker.component';
import CustomerCardComponent, { ICustomerCardContactFields } from '../customer-card/customer-card.component';
import WorkDocumentCreateComponent from '../work-document/work-document-create.component';
import LoaderComponent from '../loader/loader.component';
import ErrorMessageComponent from '../error-message/error-message.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemCliente from '../../models/ItemCliente';
import ItemOperario from '../../models/ItemOperario';
import ItemPermiso from '../../models/ItemPermiso';
import ElementEnum from '../../enums/ElementEnum';

const sectionClasses = mergeStyleSets({
    title: {
        display: 'block',
        backgroundColor: 'var(--color-primario)',
        color: 'white',
        padding: '4px 12px',
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '8px',
        marginTop: '4px',
    },
    optionalLabel: {
        fontSize: '12px',
        color: '#666',
        marginLeft: '8px',
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: 4,
        display: 'block',
    },
    buttonRow: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
    },
});

interface IWorkOrderGeneralInformationComponentProps {
    workOrder: ItemOrdenDeTrabajo;
    onWorkOrderUpdated?: () => void;
};

const WorkOrderGeneralInformationComponent = (props: IWorkOrderGeneralInformationComponentProps) => {
    const auth = useAuthStatus();
    const workOrdersPermission : ItemPermiso = auth.getPermission(ElementEnum.WORK_ORDERS);

    const [workOrder, setWorkOrder] = useState<ItemOrdenDeTrabajo>(props.workOrder);
    const [operators, setOperators] = useState<ItemOperario[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [isDocumentOpen, setIsDocumentOpen] = useState<boolean>(false);

    useEffect(() => {
        if (auth.user && auth.user.UsuarioAdmin) {
            setLoading(true);
            ApiService.Post<ItemOperario[]>("OrdenesTrabajo", "GetListaOperarios", auth.user).then((response: ItemOperario[]) => {
                setOperators(response);
                setLoading(false);
            }, (err: string) => {
                setError(err);
                setLoading(false);
            });
        }
        return () => {
            setOperators([]);
            setLoading(false);
        };
    }, [auth.user]);

    const onChangeCustomer = (item: ItemCliente) : void => {
        setWorkOrder({
            ...workOrder,
            CodigoCliente: item.CodigoCliente,
            Nombre: (item as any).Nombre || workOrder.Nombre,
            Direccion: (item as any).Direccion || workOrder.Direccion,
            CPostal: (item as any).CPostal || workOrder.CPostal,
            Poblacion: (item as any).Poblacion || workOrder.Poblacion,
            Provincia: (item as any).Provincia || workOrder.Provincia,
            Pais: (item as any).Pais || workOrder.Pais,
            Telefono: item.Telefono || workOrder.Telefono,
            Email: item.Email || workOrder.Email,
            Web: item.URL || workOrder.Web,
        });
    };

    const onContactChanged = (fields: ICustomerCardContactFields) : void => {
        setWorkOrder({
            ...workOrder,
            Nombre: fields.Nombre,
            Direccion: fields.Direccion,
            CPostal: fields.CPostal,
            Poblacion: fields.Poblacion,
            Provincia: fields.Provincia,
            Pais: fields.Pais,
            Telefono: fields.Telefono,
            Email: fields.Email,
            Web: fields.Web,
            PosicionGPS: fields.PosicionGPS,
        });
    };

    const onChangeOperator = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<any>) : void => {
        setWorkOrder({ ...workOrder, Para: option?.key?.toString() || "" });
    };

    const onSave = () : void => {
        if (auth.user) {
            setLoading(true);
            ApiService.Post<void>("OrdenesTrabajo", "Set", auth.user, {
                "ItemOrdenes": workOrder,
            }).then(() => {
                setLoading(false);
            }, (err: string) => {
                setError(err);
                setLoading(false);
            });
        }
    };

    const onCloseOrOpen = () => {
        if (!auth.user) return;
        setLoading(true);
        ApiService.Post<string>('OrdenesTrabajo', 'SetCerrada', auth.user, {
            IdOrden: workOrder.Id,
            Cerrada: !workOrder.Cerrado,
        }).then(() => {
            setLoading(false);
            if (props.onWorkOrderUpdated) props.onWorkOrderUpdated();
        }, (err: string) => {
            setError(err);
            setLoading(false);
        });
    };

    const contactDisplayText = [workOrder.Nombre, workOrder.Direccion].filter(Boolean).join(': ');

    return (
        <div className="container-fluid p-0">
            <div className="row m-0">

                <div className="col-12 py-2">
                    <div className={sectionClasses.buttonRow}>
                        {workOrdersPermission.Modificar && (
                            <DefaultButton
                                text='Guardar cambios'
                                iconProps={{ iconName: 'Save' }}
                                onClick={onSave} />
                        )}
                        {workOrdersPermission.Modificar && (
                            <DefaultButton
                                text={workOrder.Cerrado ? 'Abrir orden' : 'Cerrar orden'}
                                iconProps={{ iconName: workOrder.Cerrado ? 'LockSolid' : 'Lock' }}
                                onClick={onCloseOrOpen} />
                        )}
                        {workOrdersPermission.Modificar && (
                            <DefaultButton
                                text='Adjuntar documento'
                                iconProps={{ iconName: 'Attach' }}
                                onClick={() => setIsDocumentOpen(true)} />
                        )}
                    </div>
                </div>

                <ErrorMessageComponent message={error} />

                {/* IDENTIFICACION */}
                <div className="col-12 p-0">
                    <span className={sectionClasses.title}>Identificacion</span>
                </div>

                {/* Contacto */}
                <div className="col-12 mb-3">
                    <CustomerCardComponent
                        label='Contacto'
                        disabled={!workOrdersPermission.Modificar}
                        nombre={workOrder.Nombre}
                        direccion={workOrder.Direccion}
                        cpostal={workOrder.CPostal}
                        poblacion={workOrder.Poblacion}
                        provincia={workOrder.Provincia}
                        pais={workOrder.Pais}
                        telefono={workOrder.Telefono}
                        email={workOrder.Email}
                        web={workOrder.Web}
                        posicionGPS={workOrder.PosicionGPS}
                        displayText={contactDisplayText}
                        onCustomerSelected={onChangeCustomer}
                        onContactChanged={onContactChanged} />
                </div>

                {/* Número de documento */}
                <div className="col-12 mb-3">
                    <label className={sectionClasses.fieldLabel} htmlFor="numero-documento">Número de documento</label>
                    <TextField
                        label=''
                        value={workOrder.NumeroDocumento}
                        readOnly
                        disabled />
                </div>

                {/* Identificador externo */}
                <div className="col-12 mb-3">
                    <div className="d-flex align-items-end">
                        <div className="flex-grow-1">
                            <TextField
                                label='Identificador externo'
                                maxLength={100}
                                disabled={!workOrdersPermission.Modificar}
                                value={workOrder.IdExterno}
                                onChange={(_e, v) => setWorkOrder({ ...workOrder, IdExterno: v ?? "" })} />
                        </div>
                        <span className={sectionClasses.optionalLabel}>(opcional)</span>
                    </div>
                </div>

                {/* Fecha de alta */}
                <div className="col-auto mb-3">
                    <label className={sectionClasses.fieldLabel} htmlFor="fecha-alta">Fecha de alta</label>
                    <DatePickerComponent
                        disabled={true}
                        value={workOrder.FechaAlta}
                        onChange={() => {}} />
                </div>

                {/* Retrasar envío */}
                <div className="col-12 mb-3">
                    <Checkbox
                        label="Retrasar envio"
                        checked={workOrder.EnvioDirecto === 1}
                        disabled={!workOrdersPermission.Modificar}
                        onChange={(_e, c) => setWorkOrder({ ...workOrder, EnvioDirecto: c ? 1 : 0 })} />
                    {workOrder.EnvioDirecto === 1 && (
                        <div className="mt-2">
                            <DatePickerComponent
                                disabled={!workOrdersPermission.Modificar}
                                value={workOrder.FechaEnvio}
                                onChange={(v) => setWorkOrder({ ...workOrder, FechaEnvio: v })} />
                        </div>
                    )}
                </div>

                {/* Asignado a usuario */}
                <div className="col-12 mb-3">
                    <div className="d-flex align-items-end">
                        <div className="flex-grow-1">
                            {(auth.user && auth.user.UsuarioAdmin) ? (
                                <Dropdown
                                    label='Asignado a usuario'
                                    disabled={!workOrdersPermission.Modificar}
                                    placeholder={workOrder.Para || '(sin asignar)'}
                                    options={operators.map(o => ({
                                        key: o.IdUsuario,
                                        text: o.Nombre,
                                    }))}
                                    onChange={onChangeOperator} />
                            ) : (
                                <TextField
                                    label='Asignado a usuario'
                                    disabled={true}
                                    value={workOrder.Para} />
                            )}
                        </div>
                        <span className={sectionClasses.optionalLabel}>(opcional)</span>
                    </div>
                </div>

            </div>
            {isDocumentOpen && (
                <WorkDocumentCreateComponent
                    workOrderId={workOrder.Id}
                    onClose={() => setIsDocumentOpen(false)}
                    onWorkDocumentCreated={() => {
                        setIsDocumentOpen(false);
                        if (props.onWorkOrderUpdated) props.onWorkOrderUpdated();
                    }} />
            )}
            <LoaderComponent loading={loading} />
        </div>
    );
};

export default WorkOrderGeneralInformationComponent;